import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

// role metadata used across the UI
export const ROLE_META = {
  ceo: { label: "CEO", short: "Executive", readonly: true },
  admin: { label: "BDM", short: "Business Dev", readonly: false, admin: true },
  pmo: { label: "SA / PMO", short: "Executive", readonly: true },
  commercial: { label: "Contracts & Commercial", short: "Commercial", readonly: false, depts: ["commercial"] },
  design: { label: "Design Coordinator", short: "Design", readonly: false, depts: ["design"] },
  material: { label: "Material Engineer", short: "Material", readonly: false, depts: ["material"] },
  procurement: { label: "Procurement Head", short: "Procurement", readonly: false, depts: ["procurement"] },
  production: { label: "Production Manager", short: "Production", readonly: false, depts: ["production"] },
  projects: { label: "Projects Director", short: "Projects", readonly: false, depts: ["projects"] },
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=none, object=auth
  const [error, setError] = useState("");

  const check = useCallback(async () => {
    const token = localStorage.getItem("polaris_token");
    if (!token) { setUser(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("polaris_token");
      setUser(false);
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("polaris_token", data.token);
      setUser(data.user);
      return true;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("polaris_token");
    setUser(false);
  };

  const roleMeta = user ? ROLE_META[user.role] : null;
  const canEditDept = (dept) => !!(roleMeta && !roleMeta.readonly && roleMeta.depts?.includes(dept));
  const isReadonly = !!(roleMeta && roleMeta.readonly);
  const isAdmin = user?.role === "admin";
  // Roles that manage project-level fields (create/edit/delete/import): commercial + admin.
  const canManageProjects = !!(user && ["commercial", "admin"].includes(user.role));
  const canEditSettings = isAdmin;
  const canViewAudit = !!(user && ["admin", "ceo", "pmo"].includes(user.role));

  return (
    <AuthContext.Provider value={{ user, login, logout, error, setError, roleMeta, canEditDept, isReadonly, isAdmin, canManageProjects, canEditSettings, canViewAudit }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
